import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatApiError } from '../../lib/errors';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle, XCircle, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== CATALOG APPLICATIONS ====================
function CatalogApplicationsTab({ language }) {
  const isLV = language === 'lv';
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [actioningId, setActioningId] = useState(null);
  const [editingApp, setEditingApp] = useState(null); // catalog edit modal
  const [catalogInfo, setCatalogInfo] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const url = statusFilter === 'all'
        ? `${API}/admin/catalog-applications`
        : `${API}/admin/catalog-applications?status=${statusFilter}`;
      const { data } = await axios.get(url);
      setApps(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line
  useEffect(() => {
    axios.get(`${API}/catalog/info`).then(r => setCatalogInfo(r.data)).catch(() => {});
  }, []);

  const updateStatus = async (id, newStatus) => {
    setActioningId(id);
    try {
      await axios.put(`${API}/admin/catalog-applications/${id}`, { status: newStatus });
      await load();
    } catch (e) { alert('Kļūda: ' + (e.response?.data?.detail || e.message)); }
    finally { setActioningId(null); }
  };

  const statusBadge = (s) => {
    const map = {
      pending:  { bg: 'bg-amber-100', text: 'text-amber-800', lv: 'Gaida apstiprinājumu', en: 'Pending' },
      approved: { bg: 'bg-blue-100',  text: 'text-blue-800',  lv: 'Apstiprināts',          en: 'Approved' },
      active:   { bg: 'bg-green-100', text: 'text-green-800', lv: 'Aktīvs',                en: 'Active' },
      rejected: { bg: 'bg-red-100',   text: 'text-red-800',   lv: 'Noraidīts',             en: 'Rejected' },
      expired:  { bg: 'bg-gray-100',  text: 'text-gray-700',  lv: 'Beidzies',              en: 'Expired' },
    };
    const m = map[s] || map.pending;
    return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${m.bg} ${m.text}`}>{isLV ? m.lv : m.en}</span>;
  };

  const fmt = (d) => { try { return new Date(d).toLocaleDateString('lv-LV', { day:'2-digit', month:'2-digit', year:'numeric' }); } catch { return d; } };

  return (
    <div data-testid="catalog-applications-tab">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {['all', 'pending', 'approved', 'active', 'rejected', 'expired'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              statusFilter === s ? 'bg-[#121212] text-white border-[#121212]' : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'
            }`}
            data-testid={`catalog-filter-${s}`}
          >
            {s === 'all'
              ? (isLV ? 'Visi' : 'All')
              : (statusBadge(s).props.children)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center text-[#5C5C5C] py-10" data-testid="catalog-apps-loading">
          {isLV ? 'Ielādē...' : 'Loading...'}
        </div>
      ) : apps.length === 0 ? (
        <div className="text-center text-[#5C5C5C] py-10" data-testid="catalog-apps-empty">
          {isLV ? 'Nav pieteikumu' : 'No applications'}
        </div>
      ) : (
        <div className="space-y-3">
          {apps.map(app => (
            <div key={app.id} className="bg-white border border-[#E5E4E0] rounded-xl p-5" data-testid={`catalog-app-${app.id}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-outfit font-semibold text-[#121212] truncate">
                      {app.shop_name || app.contact_person || (isLV ? 'Nezināms veikals' : 'Unknown shop')}
                    </h3>
                    {statusBadge(app.status)}
                  </div>
                  <div className="text-xs text-[#8A8A8A] flex flex-wrap gap-x-3 gap-y-1">
                    <span>{app.email}</span>
                    <span>{app.phone}</span>
                    <span>{isLV ? 'Iesniegts:' : 'Submitted:'} {fmt(app.created_at)}</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-semibold text-[#121212]">€{(app.total_price || 0).toFixed(2)}</div>
                  <div className="text-xs text-[#5C5C5C]">{app.plan || 'standard'}</div>
                </div>
              </div>

              {app.description && (
                <p className="text-sm text-[#5C5C5C] mb-3 line-clamp-2">{app.description}</p>
              )}

              {(app.selected_catalogs?.length > 0 || app.extra_catalogs?.length > 0) && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {[...(app.selected_catalogs || []), ...(app.extra_catalogs || [])].map(c => (
                    <span key={c} className="text-[11px] px-2 py-0.5 rounded-full bg-[#FAFAFA] border border-[#E5E4E0] text-[#5C5C5C]">
                      {c}
                    </span>
                  ))}
                </div>
              )}

              {/* Actions */}
              {app.status === 'pending' && (
                <div className="flex gap-2 mt-3 pt-3 border-t border-[#F0EFEB] flex-wrap">
                  <Button
                    size="sm"
                    onClick={() => updateStatus(app.id, 'active')}
                    disabled={actioningId === app.id}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid={`approve-catalog-${app.id}`}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> {isLV ? 'Apstiprināt un aktivizēt' : 'Approve & activate'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatus(app.id, 'rejected')}
                    disabled={actioningId === app.id}
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    data-testid={`reject-catalog-${app.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-1" /> {isLV ? 'Noraidīt' : 'Reject'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingApp(app)}
                    className="border-[#D97736] text-[#D97736] hover:bg-[#FFF8F0]"
                    data-testid={`edit-catalogs-${app.id}`}
                  >
                    <BookOpen className="w-4 h-4 mr-1" /> {isLV ? 'Rediģēt katalogus' : 'Edit catalogs'}
                  </Button>
                </div>
              )}
              {app.status === 'active' && (
                <div className="flex gap-2 mt-3 pt-3 border-t border-[#F0EFEB] flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingApp(app)}
                    className="border-[#D97736] text-[#D97736] hover:bg-[#FFF8F0]"
                    data-testid={`edit-catalogs-${app.id}`}
                  >
                    <BookOpen className="w-4 h-4 mr-1" /> {isLV ? 'Rediģēt katalogus' : 'Edit catalogs'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatus(app.id, 'expired')}
                    disabled={actioningId === app.id}
                    className="border-[#E5E4E0] text-[#5C5C5C]"
                    data-testid={`expire-catalog-${app.id}`}
                  >
                    {isLV ? 'Atzīmēt kā beidzies' : 'Mark as expired'}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editingApp && catalogInfo && (
        <AdminEditCatalogsModal
          app={editingApp}
          catalogInfo={catalogInfo}
          language={language}
          onClose={() => setEditingApp(null)}
          onSaved={async () => { setEditingApp(null); await load(); }}
        />
      )}
    </div>
  );
}

function AdminEditCatalogsModal({ app, catalogInfo, language, onClose, onSaved }) {
  const isLV = language === 'lv';
  const [selected, setSelected] = useState(app.selected_catalogs || []);
  const [extra, setExtra] = useState(app.extra_catalogs || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const catNames = catalogInfo.catalog_names?.[language] || catalogInfo.catalog_names?.lv || {};
  const extraNames = catalogInfo.extra_catalog_names?.[language] || catalogInfo.extra_catalog_names?.lv || {};
  const seasons = catalogInfo.seasons || {};
  const extras = catalogInfo.extra_catalogs || [];

  const toggle = (list, setList, id) => {
    setList(list.includes(id) ? list.filter(c => c !== id) : [...list, id]);
  };

  const total = selected.length + extra.length;
  const isYearly = app.plan === 'yearly_promo';
  const estimatedPrice = isYearly
    ? catalogInfo.pricing?.yearly_promo
    : total * (catalogInfo.pricing?.monthly || 0);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API}/admin/catalog-applications/${app.id}`, {
        selected_catalogs: selected,
        extra_catalogs: extra,
      });
      await onSaved();
    } catch (e) {
      setError(formatApiError(e, isLV ? 'Kļūda' : 'Error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-edit-catalogs-modal">
        <DialogHeader>
          <DialogTitle>
            {isLV ? 'Rediģēt lietotāja katalogus' : 'Edit user catalogs'}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-[#5C5C5C] mb-3">
          {app.shop_name || app.user_name} · {app.email}
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm mb-3">{error}</div>
        )}

        {Object.entries(seasons).map(([qKey, season]) => (
          <div key={qKey} className="mb-4">
            <h4 className="font-medium text-[#5C5C5C] mb-2 text-sm italic">{season[language] || season.lv}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {season.catalogs.map(catId => (
                <label key={catId} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-[#FAFAFA] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.includes(catId)}
                    onChange={() => toggle(selected, setSelected, catId)}
                    className="rounded accent-[#D97736]"
                    data-testid={`admin-edit-cat-${catId}`}
                  />
                  <span className="text-sm">{catNames[catId] || catId}</span>
                </label>
              ))}
            </div>
          </div>
        ))}

        {extras.length > 0 && (
          <div className="mb-4">
            <h4 className="font-medium text-[#5C5C5C] mb-2 text-sm italic">
              {isLV ? 'Papildu katalogi' : 'Extra catalogs'}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {extras.map(catId => (
                <label key={catId} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-[#FAFAFA] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={extra.includes(catId)}
                    onChange={() => toggle(extra, setExtra, catId)}
                    className="rounded accent-[#D97736]"
                    data-testid={`admin-edit-extra-${catId}`}
                  />
                  <span className="text-sm">{extraNames[catId] || catId}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="p-3 bg-[#FFF8F0] border border-[#D97736]/20 rounded-xl flex items-center justify-between text-sm mb-3">
          <span className="text-[#5C5C5C]">
            {isLV ? 'Izvēlēti' : 'Selected'}: <b>{total}</b> · {isLV ? 'Plāns' : 'Plan'}: <b>{app.plan}</b>
          </span>
          <span className="font-outfit font-bold text-[#D97736]">€{estimatedPrice?.toFixed(2)}</span>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="admin-edit-catalogs-cancel">
            {isLV ? 'Atcelt' : 'Cancel'}
          </Button>
          <Button
            onClick={save}
            disabled={saving || total === 0}
            className="bg-[#D97736] hover:bg-[#C06528] text-white"
            data-testid="admin-edit-catalogs-save"
          >
            {saving ? (isLV ? 'Saglabā...' : 'Saving...') : (isLV ? 'Saglabāt' : 'Save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ==================== DANGER ZONE (production data reset) ====================

export default CatalogApplicationsTab;
